import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHome, FaInfoCircle, FaPhone, FaClipboardList, FaSignOutAlt, FaPlus, FaTrash, FaBolt, FaBuilding, FaTimes } from 'react-icons/fa';
import KruozAPI from '../../KruozAPI';
import { useAuth } from '../../AuthContext';
import { toast, Toaster } from 'react-hot-toast';
import QuotationRequestCard from './QuotationRequestCard';

// ProviderModal component
const ProviderModal = ({ isOpen, onClose, providers, onSelectProvider }) => {
  if (!isOpen) return null;;
  const getRandomColor = () => {
    const hue = Math.floor(Math.random() * 360);
    return `hsl(${hue}, 80%, 65%)`;
  };

  const getDarkerShade = (color) => {
    const hsl = color.match(/\d+/g).map(Number);
    return `hsl(${hsl[0]}, ${hsl[1]}%, ${Math.max(hsl[2] - 15, 0)}%)`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Select a Provider</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FaTimes size={24} />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {providers.map((provider) => {
            const baseColor = getRandomColor();
            const darkerColor = getDarkerShade(baseColor);
            return (
              <div
                key={provider.id}
                className="p-4 rounded-lg shadow-md cursor-pointer transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg"
                style={{
                  background: `linear-gradient(to bottom right, ${baseColor}, ${darkerColor})`,
                }}
                onClick={() => onSelectProvider(provider)}
              >
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 mr-3 flex-shrink-0 overflow-hidden bg-gray-100 rounded-full">
                    <img 
                      src={process.env.REACT_APP_IMAGE_BASE_URL + provider.logo} 
                      alt={`${provider.name} logo`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full items-center justify-center bg-gray-200 hidden rounded-full">
                      <FaBuilding className="text-gray-400 w-8 h-8" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-white text-center mb-1">{provider.name}</h3>
                  {provider.FlashQuotations && (
                    <div className="flex items-center justify-center text-yellow-300 bg-white bg-opacity-20 px-2 py-1 rounded-full text-sm">
                      <FaBolt className="mr-1" />
                      <span>Flash Quotes</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};


const CustomerHome = () => {
  const [binRequests, setQuotationRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    fetchQuotationRequests();
  }, []);

  const fetchQuotationRequests = async () => {
    if (!user || !user.id) return;
    
    setIsLoading(true);
    try {
      const response = await KruozAPI.get(`/Quotation/RequestsByCustomer?userId=${user.id}`);
      if (response.status === 200) {
        setQuotationRequests(response.data);
        response.data.forEach(request => {
          if (request.quotationCompany && request.quotationCompany.id) {
            addCompanyToProviders(request.quotationCompany);
          }
        });
      } else {
        throw new Error('Failed to fetch bin requests');
      }
    } catch (error) {
      console.error('Error fetching bin requests:', error);
      if (error.response && error.response.status === 401) {
        toast.error('Your session has expired. Please login again.');
        await logout();
        navigate('/customer-login');
      } else {
        toast.error('Failed to load bin requests. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Function to add a new company to providers
  const addCompanyToProviders = (company) => {
    setProviders(prevProviders => {
      if (!prevProviders.some(provider => provider.code === company.code)) {
        return [...prevProviders, company];
      }
      return prevProviders;
    });
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
    setIsMobileMenuOpen(false);
  };

  const confirmLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Logout failed. Please try again.');
    }
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const menuItems = [
    { icon: <FaHome className="mr-1" />, text: 'Home', href: '/' },
    { icon: <FaClipboardList className="mr-1" />, text: 'My Requests', href: '/customer-home' },
    { icon: <FaInfoCircle className="mr-1" />, text: 'About Us', href: '/about-us' },
    { icon: <FaPhone className="mr-1" />, text: 'Contact Us', href: '/contact-us' },
    { icon: <FaSignOutAlt className="mr-1" />, text: 'Logout', onClick: handleLogout },
  ];

  const handleNewRequest = () => {
    setShowProviderModal(true);
  };

  const handleSelectProvider = (provider) => {
    setShowProviderModal(false);
    navigate(`/${provider.code}`, { state: { provider: provider } });
  };

  const handleViewQuotation = (quotationId) => {
    navigate(`/quotation/${quotationId}`);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <Toaster position="top-right" />
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <img
                  src={process.env.REACT_APP_KRUOZ_IMAGE_BASE_URL + 'EasyHireQuates.png'}
                  alt="EasyHireQuotes"
                  className="h-12 w-auto"
                />
          </div>
          <nav className="hidden md:flex space-x-4">
            <a href="/" className="flex items-center text-gray-600 hover:text-green-600 transition-colors">
              <FaHome className="mr-1" />
              <span>Home</span>
            </a>
            <button onClick={handleLogout} className="flex items-center text-gray-600 hover:text-green-600 transition-colors">
              <FaSignOutAlt className="mr-1" />
              <span>Logout</span>
            </button>
          </nav>
        </div>
        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            {menuItems.map((item, index) => (
              <div key={index} className="block py-2 px-4 text-gray-600 hover:bg-gray-100 hover:text-green-600 transition-colors">
                {item.onClick ? (
                  <button 
                    onClick={() => {
                      item.onClick();
                      setIsMobileMenuOpen(false);
                    }} 
                    className="w-full text-left flex items-center"
                  >
                    {item.icon}
                    {item.text}
                  </button>
                ) : (
                  <a 
                    href={item.href} 
                    className="flex items-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.icon}
                    {item.text}
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Your Dashboard</h1>
          <button 
            onClick={handleNewRequest}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-full shadow-lg transform transition hover:scale-105 flex items-center"
          >
            <FaPlus className="mr-2" /> New Quotation Request
          </button>
        </div>
        
        {/* Quotation Requests Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">Your Quote Requests</h2>
          {isLoading ? (
            <div className="text-center mt-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            </div>
          ) : binRequests.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {binRequests.map((request) => (
                <QuotationRequestCard
                  key={request.id}
                  request={request}
                  onViewQuotation={handleViewQuotation}
                />
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 bg-gray-50 rounded-lg p-8">
              <FaTrash className="text-5xl mx-auto mb-4 text-gray-400" />
              <p className="text-xl">You haven't made any bin requests yet.</p>
              <p className="mt-2">Click the "New Quote Request" button to get started!</p>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="mb-4">© {new Date().getFullYear()} EasyHireQuotes. All rights reserved.</p>
          <div className="flex justify-center space-x-4">
            {/* <a href="/privacy-policy" className="hover:text-green-400 transition-colors">Privacy Policy</a>
            <a href="/terms-of-service" className="hover:text-green-400 transition-colors">Terms of Service</a>
            <a href="/contact" className="hover:text-green-400 transition-colors">Contact</a> */}
          </div>
        </div>
      </footer>

      {/* Logout Confirmation Dialog */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl">
            <h2 className="text-xl font-bold mb-4">Confirm Logout</h2>
            <p className="mb-4">Are you sure you want to log out?</p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={cancelLogout}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Provider Selection Modal */}
      <ProviderModal
        isOpen={showProviderModal}
        onClose={() => setShowProviderModal(false)}
        providers={providers}
        onSelectProvider={handleSelectProvider}
      />
    </div>
  );
};

export default CustomerHome;